import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Target, Check, Plus, X } from 'lucide-react';
import { elodge } from '@/lib/api';

const DEFAULT_ROWS = [
  { subclass: '189', suggested: 240 },
  { subclass: '482', suggested: 180 },
  { subclass: '500', suggested: 120 },
];

export default function FleetSlaCard() {
  const [rows, setRows] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [newSub, setNewSub] = React.useState('');
  const [newMin, setNewMin] = React.useState('');

  const load = React.useCallback(async () => {
    try {
      const r = await elodge.sla.get();
      const t = r.targets || {};
      const list = Object.entries(t).map(([subclass, minutes]) => ({ subclass, minutes: String(minutes) }));
      // Seed placeholder rows for the common subclasses if none saved yet
      if (list.length === 0) {
        setRows(DEFAULT_ROWS.map((d) => ({ subclass: d.subclass, minutes: '' })));
      } else {
        setRows(list);
      }
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to load' });
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const targets = {};
      for (const r of rows) {
        const m = parseFloat(r.minutes);
        if (r.subclass && !isNaN(m) && m > 0) targets[r.subclass] = m;
      }
      await elodge.sla.save(targets);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Save failed' });
    } finally { setBusy(false); }
  };

  const addRow = () => {
    const s = newSub.trim(); const m = parseFloat(newMin);
    if (!s || isNaN(m) || m <= 0) return;
    if (rows.some((r) => r.subclass === s)) { setMsg({ ok: false, text: `Subclass ${s} already listed` }); return; }
    setRows((r) => [...r, { subclass: s, minutes: String(m) }]);
    setNewSub(''); setNewMin('');
  };
  const removeRow = (sub) => setRows((r) => r.filter((x) => x.subclass !== sub));

  return (
    <Card className="border-slate-200" data-testid="fleet-sla-card"><CardContent className="p-6">
      <div className="flex items-center gap-2">
        <Target className="h-4 w-4 text-[#2ECC9A]" />
        <div className="text-sm font-semibold text-[#1A3A5C]">Lodgement SLA targets</div>
        {saved && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]"><Check className="mr-1 h-3 w-3"/> Saved</Badge>}
      </div>
      <p className="mt-2 text-xs text-slate-500">
        Set an acceptable p95 wall-clock time (minutes) from job enqueue → succeeded, per DHA subclass.
        If the rolling 30-day p95 breaches the target, a bell alert fires — once per subclass per day.
      </p>

      <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Subclass</th>
              <th className="px-3 py-2 font-medium">Target (minutes)</th>
              <th className="px-3 py-2 font-medium" />
            </tr>
          </thead>
          <tbody data-testid="fleet-sla-rows">
            {rows.map((r) => (
              <tr key={r.subclass} className="border-t border-slate-100" data-testid={`fleet-sla-row-${r.subclass}`}>
                <td className="px-3 py-2 font-mono">{r.subclass}</td>
                <td className="px-3 py-2">
                  <Input value={r.minutes} onChange={(e) => setRows((rs) => rs.map((x) => x.subclass === r.subclass ? { ...x, minutes: e.target.value } : x))}
                         data-testid={`fleet-sla-input-${r.subclass}`}
                         type="number" min={1}
                         className="h-8 w-32 text-xs" />
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => removeRow(r.subclass)} className="text-slate-400 hover:text-red-500"
                          data-testid={`fleet-sla-remove-${r.subclass}`}>
                    <X className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex gap-1.5">
        <Input value={newSub} onChange={(e) => setNewSub(e.target.value)} placeholder="Subclass e.g. 190"
               data-testid="fleet-sla-new-sub" className="h-8 w-32 text-xs" />
        <Input value={newMin} onChange={(e) => setNewMin(e.target.value)} placeholder="Minutes"
               type="number" min={1}
               data-testid="fleet-sla-new-min" className="h-8 w-24 text-xs" />
        <Button size="sm" variant="outline" onClick={addRow} data-testid="fleet-sla-add"
                className="h-8 gap-1 text-xs">
          <Plus className="h-3 w-3" /> Add
        </Button>
      </div>

      {msg && (
        <div data-testid="fleet-sla-msg"
             className={`mt-3 rounded-md p-2 text-xs ${msg.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      <div className="mt-4">
        <Button data-testid="fleet-sla-save" onClick={save} disabled={busy}
                className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save SLA targets'}
        </Button>
      </div>
    </CardContent></Card>
  );
}
